import styled from '@emotion/styled';
import { Button, Input, Modal, ModalProps } from 'antd';
import useShopeeAuthShops from 'hooks/business/authShops/useShopeeAuthShops';
import useShopeeImageCaptcha from 'hooks/business/authShops/useShopeeImageCaptcha';
import useShopeeSmsCaptcha from 'hooks/business/authShops/useShopeeSmsCaptcha';
import React, { useMemo } from 'react';
import { useTranslate } from '@tolgee/react';

interface Props extends ModalProps {}

const AccountText = styled.div`
  font-size: 24px;
  color: var(--fett-text-primary);
  margin-bottom: 10px;
`;
const Tips = styled.div`
  display: inline-block;
  height: 28px;
  line-height: 28px;
  background: var(--fett-bg-info-tip);
  border-radius: 2px;
  font-size: 12px;
  color: var(--fett-scenario-accent-text);
  padding: 0 10px;
  margin-bottom: 10px;
`;
const SMSCaptchaInput = styled(Input)`
  width: 200px;
`;
const Content = styled.div`
  .ant-btn {
    margin-right: 8px;d
    margin-bottom: 12px;
  }
`;
const OperationWrap = styled.div`
  margin-top: 32px;
`;
const CountdownText = styled.span`
  height: 32px;
  line-height: 32px;
  font-size: 12px;
  color: var(--fett-text-tertiary);
  margin-left: 8px;
`;

const AuthCaptchaModal = (props: Props) => {
  const { authCaptchaState, skipCaptcha, countdownFormatter } = useShopeeAuthShops();
  const { t } = useTranslate();
  useShopeeImageCaptcha();
  const { smsInputValue, onChange, submitSms } = useShopeeSmsCaptcha();

  const countdownText = useMemo(() => {
    return countdownFormatter ? (
      <CountdownText>
        {t('ViewShops.shop.auth.captcha.countdown.text', { x: countdownFormatter })}
      </CountdownText>
    ) : null;
  }, [countdownFormatter, t]);

  const content = useMemo(() => {
    if (authCaptchaState.authAgent) {
      const { status } = authCaptchaState.authAgent;
      // 短信验证码
      if (status === 'WaitForOTP' || status === 'WaitForOTPRetry') {
        return (
          <Content>
            <div>
              <Tips>{t('ViewShops.shopee.auth.captcha.sms.tips')}</Tips>
            </div>
            <SMSCaptchaInput
              value={smsInputValue}
              onChange={onChange}
              placeholder={t('ViewShops.shopee.auth.captcha.sms.input.placeholder')}
              maxLength={6}
            />
            <OperationWrap>
              <Button type="primary" disabled={!smsInputValue} onClick={submitSms}>
                {t('ViewShops.shopee.auth.captcha.confirm')}
              </Button>
              <Button onClick={skipCaptcha}>{t('ViewShops.shopee.auth.captcha.skip')}</Button>
              {countdownText}
            </OperationWrap>
          </Content>
        );
      }

      // 图片验证码
      if (status === 'WaitForImageCaptcha') {
        return (
          <Content>
            <Tips>{t('ViewShops.shopee.auth.captcha.image.tips')}</Tips>
            <OperationWrap>
              <Button onClick={skipCaptcha}>{t('ViewShops.shopee.auth.captcha.skip')}</Button>
              {countdownText}
            </OperationWrap>
          </Content>
        );
      }
    }
    return null;
  }, [
    authCaptchaState.authAgent,
    smsInputValue,
    onChange,
    t,
    submitSms,
    skipCaptcha,
    countdownText,
  ]);

  return (
    <Modal
      title={t('ViewShops.store.auth.modal.title')}
      visible={authCaptchaState.visible}
      width={400}
      footer={null}
      closable={false}
      zIndex={1010}
      {...props}
    >
      <div>{t('ViewShops.shopee.auth.captcha.account')}</div>
      <AccountText>{authCaptchaState.authAgent?.info.account}</AccountText>
      {content}
    </Modal>
  );
};

export default AuthCaptchaModal;
